import type { Flag } from '@/state/flags/flag-types';
import { ExternalLink, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { pageUrlFor } from '@/state/editor-link/editor-link';
import { useEditorLink } from '@/state/editor-link/useEditorLink';
import { FlagSection } from './FlagSection';
import { FLAG_COPY } from '../copy';

type FlagEditorChangesProps = { flag: Flag };

const COPY = FLAG_COPY.editor;

/** The same listing the dashboard's plugin notice links to. */
const EXTENSION_URL = 'https://chromewebstore.google.com/detail/pertento-editor/fjooappefigppfjolhadliepialebodg';

/**
 * An editor flag's On side: the visual-editor changes it applies. The editor opens from the
 * Pertento Editor extension on the site itself, which lists editor flags with the website's
 * experiments. An extension that takes the dashboard's Open in editor (state/editor-link/)
 * gets a button that opens the flag's page with its On variant picked; an older one, or none,
 * gets the site and a line saying what to pick.
 */
export const FlagEditorChanges = ({ flag }: FlagEditorChangesProps) => {
  const editorLink = useEditorLink();
  const on = flag.variants.find((variant) => variant.key === 'on');
  const changes = on?.changes?.length ?? 0;
  const siteUrl = flag.website?.url ?? null;
  // The flag's own page where its URL targeting names one, else the website.
  const pageUrl = pageUrlFor({ urlTargeting: flag.urlTargeting, websiteUrl: siteUrl });
  const canLink = editorLink.isSupported && !!on && !!pageUrl && !!flag.websiteId;

  const openInEditor = () => {
    if (!on || !pageUrl || !flag.websiteId) return;
    editorLink.open({ websiteId: flag.websiteId, experimentId: flag.id, variantId: on.id, url: pageUrl });
  };

  return (
    <FlagSection title={COPY.title} help={COPY.help} description={COPY.description} tourAnchor="flag-editor">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2.5">
          <PenLine className="text-dim size-4 shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <p className="text-foreground m-0 text-body font-medium">{COPY.changes(changes)}</p>
            {canLink ? (
              <p className="supporting m-0">{COPY.openInEditorHow}</p>
            ) : (
              <p className="supporting m-0">
                {COPY.how}{' '}
                <a
                  href={EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:text-foreground"
                >
                  {COPY.install}
                </a>
              </p>
            )}
          </div>
        </div>
        {canLink ? (
          <Button type="button" onClick={openInEditor} className="h-8 shrink-0 rounded-lg px-3 text-meta">
            <PenLine className="size-3.5" strokeWidth={1.5} />
            {COPY.openInEditor}
          </Button>
        ) : (
          siteUrl && (
            <Button asChild className="h-8 shrink-0 rounded-lg px-3 text-meta">
              <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="size-3.5" strokeWidth={1.5} />
                {COPY.open}
              </a>
            </Button>
          )
        )}
      </div>
    </FlagSection>
  );
};
